import { Offcanvas, Row } from 'react-bootstrap';
import React, { useState } from 'react';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { FormProvider, useForm } from 'react-hook-form';
import { EditEntityDrawerProps } from './EntityListProps';
import DrawerWrapper, { DrawerFooter } from './DrawerWrapper';
import FormTextField from './formFields/FormTextField';
import FormTextAreaField from './formFields/FormTextAreaField';
import { ResourceSite, useUpdateResourceSite } from './hooks/resourceSiteHooks';
import FormSelectField from './formFields/FormSelectField';
import GeoLocationComponent from 'components/GeoLocationComponent/GeoLocationComponent';
import { useCustomResourceTypes } from './hooks/customResourceSiteTypeHooks';
import { RESOURCE_SITE_TYPES } from 'components/IAP/ICSConstants';
import CustomResourceSiteTypesAddDrawer from './CustomResourceSiteTypesAddDrawer';

type FormFields = {
  name: string;
  description: string;
  geolocation?: string;
  type:
    | 'Staging Area'
    | 'Incident Command Post'
    | 'Warehouse'
    | 'Hotel/Lodging';
  archived: boolean;
};

const ResourceSiteEditDrawer: React.FC<EditEntityDrawerProps<ResourceSite>> = ({
  toggle,
  selectedEntity: editedEntity,
}) => {
  const form = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: {
      ...editedEntity,
    },
  });
  const { handleSubmit, formState } = form;

  const mutation = useUpdateResourceSite(editedEntity.id);

  const [geolocation, setGeolocation] = useState(editedEntity.geolocation);
  const [watchLocation, setWatchLocation] = useState(false);

  const submitResourceSiteEdit = async (formData: FormFields) => {
    const payload = {
      id: editedEntity.id,
      ...formData,
      archived: false,
      geolocation: geolocation,
    };
    await mutation.mutateAsync(payload);
    toggle();
  };

  const toggleActive = async () => {
    await mutation.mutateAsync({
      ...editedEntity,
      archived: !editedEntity.archived,
    });
    toggle();
  };

  const [
    showCustomResourceSiteTypeDrawer,
    setShowCustomResourceSiteTypeDrawer,
  ] = useState(false);

  const { data: customResourceSiteTypes = [] } = useCustomResourceTypes();

  return (
    <DrawerWrapper toggle={toggle} title="Edit Resource Site">
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(submitResourceSiteEdit)}
          className="d-flex flex-column justify-content-between flex-grow-1"
        >
          <Offcanvas.Body>
            <Row>
              <FormTextField
                title="Name"
                fieldName="name"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  maxLength: {
                    value: 300,
                    message: 'The maximum length is 300 characters',
                  },
                }}
                variant="half"
              />
            </Row>
            <Row>
              <FormTextAreaField
                title="Description"
                fieldName="description"
                variant="full"
              />
            </Row>
            <Row>
              <FormSelectField
                title="Site Type"
                fieldName="type"
                rules={{
                  required: { value: true, message: 'This field is required' },
                }}
                options={[
                  ...RESOURCE_SITE_TYPES,
                  ...(customResourceSiteTypes
                    ?.filter((r) => !r.archived)
                    .map((r) => ({ label: r.name, value: r.id })) || []),
                ]}
                variant="half"
              />
              <span
                role="button"
                className="text-primary cursor-pointer"
                onClick={() => setShowCustomResourceSiteTypeDrawer(true)}
              >
                Add Custom Resource Site Type
              </span>
            </Row>
            <Row>
              <div className="mt-4">
                <label className="form-label">Location:</label>
                <GeoLocationComponent
                  location={geolocation}
                  setLocation={setGeolocation}
                  setWatchLocation={setWatchLocation}
                />
              </div>
            </Row>
          </Offcanvas.Body>
          <DrawerFooter>
            <div className="button-group">
              <StylishNewButton
                className="button--tertiary button--reverse"
                onClick={toggleActive}
              >
                {(!editedEntity.archived && 'Deactivate') || 'Activate'}
              </StylishNewButton>
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={toggle}
              >
                Close
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={
                  mutation.isPending || !(formState.isDirty || watchLocation)
                }
              >
                Save
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>
      {showCustomResourceSiteTypeDrawer && (
        <CustomResourceSiteTypesAddDrawer
          toggle={() => setShowCustomResourceSiteTypeDrawer(false)}
        />
      )}
    </DrawerWrapper>
  );
};

export default ResourceSiteEditDrawer;
